import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import BookMyOpAPI from '../services/BookMyOpapi';

const NameRequiredDialog = () => {
    const { user, refreshProfile, isAuthenticated } = useBookMyOpAuth();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Check if name is missing whenever user data changes
    useEffect(() => {
        if (isAuthenticated && user) {
            // Check if name is missing or empty
            const hasName = user.name && user.name.trim() !== '';
            if (!hasName) {
                setOpen(true);
                // Pre-fill email if available
                setEmail(user.email || '');
            } else {
                setOpen(false);
            }
        }
    }, [user, isAuthenticated]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate name
        if (!name.trim()) {
            setError('Name is required');
            return;
        }

        // Validate email
        if (!email.trim()) {
            setError('Email is required');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setError('Please enter a valid email address');
            return;
        }

        setError('');
        setLoading(true);

        try {
            // Prepare update data
            const formData = new FormData();
            formData.append('name', name.trim());
            formData.append('email', email.trim());

            // Update profile
            const response = await BookMyOpAPI.updateProfile(formData, { 'Content-Type': 'multipart/form-data' });
            
            // Update localStorage immediately for instant reflection
            if (response.data) {
                localStorage.setItem('bookmyop_user_data', JSON.stringify(response.data));
            }
            
            // Refresh profile to get updated data
            await refreshProfile(true);
            
            // Close dialog
            setOpen(false);
            setName('');
            setEmail('');
        } catch (err) {
            console.error('Error updating profile:', err);
            setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                // Prevent closing by backdrop click or escape key
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    padding: 2
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                    Complete Your Profile
                </Typography>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                        Please enter your name and email to continue using the app. This helps us personalize your experience.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            required
                            fullWidth
                            label="Full Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={loading}
                            autoFocus
                            placeholder="Enter your full name"
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                }
                            }}
                        />

                        <TextField
                            required
                            fullWidth
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            placeholder="Enter your email"
                            variant="outlined"
                            helperText="We'll use this to send you important updates"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                }
                            }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ justifyContent: 'center', pb: 2, pt: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading || !name.trim() || !email.trim()}
                        sx={{
                            minWidth: 200,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1.5
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                Saving...
                            </>
                        ) : (
                            'Continue'
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default NameRequiredDialog;

